// The booking rate (BL-003): over a range of the company's days, by the person who took the calls,
// by reason, or by campaign. A CSR sees their own; owners and managers see everyone's, with how many
// of each person's reasons were changed in review, so a pattern stands out. By reason over a month,
// the after-hours calls a company might start a service for.

import { useState } from 'react';
import { rangeOf, rateLine, ratePct, takersNote, type RangeKey } from './reasons-model.ts';
import {
  BYS,
  changedWord,
  countsLine,
  missedLine,
  RANGES,
  useBookingRate,
  useMyRate,
  type By,
} from './use-booking-rate.ts';
import './booking-rate.css';

/** "This week: 6 of 8 leads booked (75%)": the CSR's own line, or everyone's for a reviewer. */
export function MyRate({
  today,
  everyone,
  version,
}: {
  today: string;
  everyone: boolean;
  /** Changes when a call's reason, outcome or review may have: the line is read again (P-0052). */
  version: string;
}) {
  const line = useMyRate(today, everyone, version);
  if (!line) return null;
  return (
    <p className="my-rate" role="status">
      {line}
    </p>
  );
}

export function BookingRate({ today, everyone }: { today: string; everyone: boolean }) {
  const [range, setRange] = useState<RangeKey>('week');
  const [by, setBy] = useState<By>('csr');
  const { r, error } = useBookingRate(range, by, today);
  return (
    <section className="stone card booking-rate" aria-label="Booking rate">
      <div className="row between wrap">
        <h2 className="h4">Booking rate</h2>
        <div className="row wrap">
          <label className="f inline">
            <span className="flabel">When</span>
            <select className="field" value={range} onChange={(e) => setRange(e.target.value as RangeKey)}>
              {RANGES.map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          {everyone && (
            <label className="f inline">
              <span className="flabel">Show</span>
              <select className="field" value={by} onChange={(e) => setBy(e.target.value as By)}>
                {BYS.map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>
      <p className="muted small">
        Booked ÷ the leads that count: a call under a minute, or with an excused reason (after hours, say),
        doesn’t count against whoever took it.
      </p>
      {error && <p className="error">{error}</p>}
      {!r && !error && <p className="muted">Reading the calls…</p>}
      {r && (
        <>
          <ul className="rate-rows">
            {(everyone ? [r.total, ...r.rows] : r.rows).map((x, i) => (
              <li
                key={`${x.key ?? 'none'}-${x.label}-${i}`}
                className={`rate-row${i === 0 && everyone ? ' total' : ''}`}
              >
                <div className="rate-head">
                  <b>{x.label}</b>
                  {by !== 'reason' && <span className="rate-pct">{ratePct(x.rate)}</span>}
                </div>
                <span>
                  {by === 'reason' ? `${x.taken} ${x.taken === 1 ? 'call' : 'calls'}` : rateLine(x)}
                </span>
                <span className="muted small">{countsLine(x)}</span>
                {changedWord(x) && <span className="chip say warn">{changedWord(x)}</span>}
              </li>
            ))}
          </ul>
          {everyone && missedLine(r.missed) && <p className="muted small">{missedLine(r.missed)}</p>}
          {by === 'csr' && takersNote(rangeOf(range, today).from, r.takersSince, r.rows) && (
            <p className="muted small">{takersNote(rangeOf(range, today).from, r.takersSince, r.rows)}</p>
          )}
          {!r.rows.length && <p className="muted">No calls taken in this range.</p>}
        </>
      )}
    </section>
  );
}
